import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MOODS, type MoodId } from "@wiseowl/design-system";
import {
  AuthShell, FeatIcon, LOGO_SRC, SvgArrow,
} from "./AuthShell.js";
import { useAuth } from "../../lib/auth-context.js";
import { api } from "../../lib/api.js";

/*
 * /welcome — first-reflection landing.
 *
 * By the time we render here the user already has a session (from
 * /verify). Picking a mood logs a real baseline via POST /api/moods
 * (source = baseline); "Begin" then navigates to /home where the
 * Daily Reset takes over.
 */
export const Welcome = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const name = user?.name ?? sessionStorage.getItem("wo.auth.name") ?? "friend";

  const [mood, setMood] = useState<MoodId>("tired");
  const [submitting, setSubmitting] = useState(false);

  const goHome = async () => {
    if (submitting) return;
    setSubmitting(true);
    // Best-effort baseline mood log — the user is authed, so this hits the real API.
    await api("/moods", { method: "POST", body: JSON.stringify({ moodSlug: mood, source: "baseline" }) }).catch(() => undefined);
    sessionStorage.removeItem("wo.auth.email");
    sessionStorage.removeItem("wo.auth.name");
    navigate("/home", { replace: true });
  };

  return (
    <AuthShell
      brand={{
        eyebrow: "You're in",
        quote: <>Take three slow breaths. Notice what&apos;s <em>already here</em> before you begin.</>,
        cite: "— Your first reflection",
        features: [
          { icon: FeatIcon.spark, label: "Your space is ready" },
        ],
      }}
      topRight={<Link to="/profile" className="btn-ghost">Profile</Link>}
      footer={<>Wrong account? <Link to="/sign-in">Sign in as someone else →</Link></>}
    >
      <div style={{ textAlign: "center", width: "100%" }}>
        <div className="breath-ring">
          <div className="core"><img src={LOGO_SRC} alt="Wise Owl Talks" /></div>
        </div>

        <div className="eyebrow mustard">You&apos;re in</div>
        <h1 className="wo-title" style={{ marginTop: 6 }}>Welcome, {name}.</h1>
        <p className="wo-sub">Your space is ready. Before you begin — one small thing.</p>

        <div style={{ textAlign: "left", margin: "26px 0 20px" }}>
          <div className="eyebrow">How are you arriving today?</div>
          <p style={{ fontSize: 13.5, color: "rgb(var(--wo-muted))", margin: "6px 0 16px" }}>
            Pick the one closest. This becomes the first dot on your reflection map.
          </p>

          <div className="mood-grid">
            {MOODS.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setMood(m.id)}
                className={`mood-pick${mood === m.id ? " active" : ""}`}
              >
                <span className="mood-disc" style={{ background: m.hex }} />
                <span className="mood-name">{m.name}</span>
              </button>
            ))}
          </div>
        </div>

        <button type="button" className="btn-primary" onClick={goHome} disabled={submitting}>
          {submitting ? "Setting up your space…" : "Begin today's reset"}
          {SvgArrow}
        </button>

        <button
          type="button"
          className="btn-ghost"
          onClick={goHome}
          style={{ width: "100%", marginTop: 14, height: 44, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
        >
          Just take me home
        </button>

        <p className="reflect">&ldquo;You don&apos;t have to feel ready. You only have to show up.&rdquo;</p>
      </div>
    </AuthShell>
  );
};
